/**
 * @author Milton Filho
 */
import { combineReducers } from 'redux';
import { Action, handleActions } from 'redux-actions';
import { OfflineQueueItem, OfflineSyncState } from 'modules/offlineSync/types';
import {
  CLEAR_FAILED_ITEM,
  DISCARD_OFFLINE_BOOKING,
  ENQUEUE_OFFLINE_ITEM,
  OFFLINE_BOOK_APPOINTMENT_ENQUEUED,
  SYNC_BOOKING_SUCCESS,
  SYNC_COMPLETE,
  SYNC_ITEM_FAILURE,
  SYNC_ITEM_SUCCESS,
  SYNC_START,
} from 'modules/offlineSync/actionTypes';
import {
  ClearFailedItemPayload,
  DiscardOfflineBookingPayload,
  OfflineBookAppointmentEnqueuedPayload,
  SyncBookingSuccessPayload,
  SyncItemFailurePayload,
  SyncItemSuccessPayload,
} from 'modules/offlineSync/actions';

const queue = handleActions<OfflineSyncState['queue'], any>(
  {
    [ENQUEUE_OFFLINE_ITEM]: (state, action: Action<OfflineQueueItem>) => [
      ...state,
      action.payload,
    ],
    [SYNC_ITEM_SUCCESS]: (state, action: Action<SyncItemSuccessPayload>) =>
      state.filter(item => item.id !== action.payload.queueItemId),
    [SYNC_BOOKING_SUCCESS]: (
      state,
      action: Action<SyncBookingSuccessPayload>,
    ) => state.filter(item => item.id !== action.payload.queueItemId),
    [SYNC_ITEM_FAILURE]: (state, action: Action<SyncItemFailurePayload>) =>
      state.map(item =>
        item.id === action.payload.queueItemId
          ? { ...item, failedAt: new Date().toISOString() }
          : item,
      ),
    [CLEAR_FAILED_ITEM]: (state, action: Action<ClearFailedItemPayload>) =>
      state.filter(item => item.id !== action.payload.queueItemId),
    // Same removal from the queue as `CLEAR_FAILED_ITEM`. The two differ in
    //   what they leave behind, not in what they take out of the queue: see the
    //   `pendingIds` case below and the `appointments` reducer.
    [DISCARD_OFFLINE_BOOKING]: (
      state,
      action: Action<DiscardOfflineBookingPayload>,
    ) => state.filter(item => item.id !== action.payload.queueItemId),
  },
  [],
);

const pendingIds = handleActions<OfflineSyncState['pendingIds'], any>(
  {
    [ENQUEUE_OFFLINE_ITEM]: (state, action: Action<OfflineQueueItem>) => [
      ...state,
      ...action.payload.tempAppointmentIds,
    ],
    [SYNC_ITEM_SUCCESS]: (state, action: Action<SyncItemSuccessPayload>) =>
      state.filter(id => !action.payload.pendingIdsToRemove.includes(id)),
    [SYNC_BOOKING_SUCCESS]: (
      state,
      action: Action<SyncBookingSuccessPayload>,
    ) => state.filter(id => !action.payload.tempIds.includes(id)),
    [CLEAR_FAILED_ITEM]: (state, action: Action<ClearFailedItemPayload>) => {
      // The id stays marked on purpose. This action fires on a conflict for a
      //   booking we probably did create, where the optimistic card is the only
      //   trace of an appointment our schedule cannot see — so removing the mark
      //   would hide it.
      //
      // It does **not** stay "until page reload", which is what this comment
      //   used to claim. `store.ts` persists the whole state bar `evalSession`,
      //   so `pendingIds` is rehydrated and the mark survives reloads, app
      //   restarts, everything. It is permanent until a schedule refetch turns
      //   up the real appointment.
      //
      // That is why the card cannot read this as "Sync pending": taking the item
      //   out of the queue above means no reconnect will ever replay it.
      //   `getAppointmentSyncMark` derives `unconfirmed` from exactly this
      //   pair — id still marked, no queue item referencing it.
      return state;
    },
    // Here the card is going away, so its pending mark has to go with it —
    //   otherwise `pendingIds` keeps ids for appointments that no longer exist.
    [DISCARD_OFFLINE_BOOKING]: (
      state,
      action: Action<DiscardOfflineBookingPayload>,
    ) => state.filter(id => !action.payload.tempAppointmentIds.includes(id)),
  },
  [],
);

const tempIdCounter = handleActions<OfflineSyncState['tempIdCounter'], any>(
  {
    [OFFLINE_BOOK_APPOINTMENT_ENQUEUED]: (
      state,
      action: Action<OfflineBookAppointmentEnqueuedPayload>,
    ) => state - action.payload.tempAppointments.length,
  },
  -1,
);

const syncStatus = handleActions<OfflineSyncState['syncStatus'], any>(
  {
    [SYNC_START]: () => 'syncing' as const,
    [SYNC_COMPLETE]: () => 'idle' as const,
  },
  'idle',
);

export type { OfflineSyncState };

export default combineReducers({
  queue,
  pendingIds,
  tempIdCounter,
  syncStatus,
});
